/**
 * Géométrie du brain — projection des coordonnées de l'item vers l'écran.
 *
 * Aucune dépendance : ni Vue, ni DOM, ni moteur de rendu. C'est du calcul pur,
 * donc testable et réutilisable.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TROIS ESPACES DE COORDONNÉES
 *
 * 1. ESPACE ITEM (ce que l'API envoie) — hérité de l'export Unity.
 *    1700 x 964, origine au CENTRE, axe Y vers le HAUT.
 *
 * 2. ESPACE SCÈNE (ce qu'on calcule ici) — 1920 x 1080 px, origine en HAUT à
 *    GAUCHE, axe Y vers le BAS, comme le DOM.
 *
 * 3. ESPACE ÉCRAN — la scène, mise à l'échelle par un facteur unique
 *    (cf. BrainStage.vue). Tout étant exprimé en px de scène, une seule
 *    transformation suffit et rien ne se déforme.
 *
 * La conversion 1 -> 2 est celle de brain.js (normalizePos:1898 puis les
 * formules de createMask/createToken/createFigures/createDropzone), à ceci
 * près qu'on la fait une fois, au même endroit, au lieu de la réécrire dans
 * quatre fonctions.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Espace de l'item, tel que produit par l'export Unity (brain.js:39-40). */
export const ITEM_WIDTH = 1700
export const ITEM_HEIGHT = 964

/** Espace de la scène. brain.js forçait le renderer à cette taille (brain.js:373). */
export const STAGE_WIDTH = 1920
export const STAGE_HEIGHT = 1080

/** Côté des tokens, figures et dropzones (brain.js:955, 1031, 1074). */
export const SPRITE_SIZE = 100

/**
 * Rayon des coins pour les masques de type `Rectangle`.
 *
 * brain.js en fait un `PIXI.NineSlicePlane(mask2.png, 50, 50, 50, 50)`, dont
 * les coins ne sont jamais étirés. Mesure de l'alpha du PNG : l'arc est un
 * cercle de 48px sur une image 150x150 (RMS 0,90px). Or `border-radius` en px
 * a exactement la même propriété — d'où l'absence d'image pour ce type.
 */
export const NINE_SLICE_RADIUS = 48

/** Les seuls types de masque rendus en nine-slice par brain.js (1462-1471). */
const NINE_SLICE_TYPES = new Set(["Rectangle"])

// ──────────────────────────────────────────────────────────── données brutes

export type RawMask = {
  name: string
  type: string
  width: number
  height: number
  /** En degrés, sens horaire inversé (brain.js applique degToRad(-rotation)). */
  rotation: number
  x: number
  y: number
}

export type RawSprite = {
  name: string
  type?: string
  /** Nom du masque parent, pour les figures et les dropzones. */
  parent?: string
  x: number
  y: number
}

export type RawSceneData = {
  masks?: RawMask[]
  tokens?: RawSprite[]
  figures?: RawSprite[]
  dropzones?: RawSprite[]
}

// ────────────────────────────────────────────────────────────── mise en page

export type SpriteLayout = {
  /** Clé stable pour le `v-for` et pour la sonde de parité. */
  key: string
  name: string
  /** Type normalisé en minuscules ("abstract" | "numeric"). */
  type: string
  /** Centre du sprite, en px de scène (ou locaux au masque pour un enfant). */
  x: number
  y: number
  size: number
  /**
   * Contre-rotation, en radians. Annule celle du masque parent pour que le
   * sprite reste droit (brain.js:1530). Vaut 0 pour un token.
   */
  counterRotation: number
}

export type MaskLayout = {
  name: string
  type: string
  /** Coin haut-gauche de la boîte, en px de scène. */
  left: number
  top: number
  width: number
  height: number
  /** Rotation en radians, autour du centre de la boîte. */
  rotation: number
  /** Si vrai : coins à rayon fixe, pas d'image de masque. */
  isNineSlice: boolean
  figures: SpriteLayout[]
  dropzones: SpriteLayout[]
}

export type SceneLayout = {
  masks: MaskLayout[]
  /** Les tokens vivent dans l'espace scène, jamais dans un masque. */
  tokens: SpriteLayout[]
  /**
   * Les mêmes figures et dropzones que dans `masks[]`, mais à plat et dans
   * l'ORDRE DES DONNÉES — pas regroupées par masque.
   *
   * Pourquoi cette redondance : les animations d'entrée et de sortie décalent
   * chaque élément de `index * 0.1` seconde (brain.js:747-777 et 814-855), en
   * parcourant les tableaux plats `this.figures` / `this.dropzones`, remplis
   * dans l'ordre du JSON. Le regroupement par masque perd cet ordre dès qu'un
   * item a plusieurs masques — ce qui arrive en production (3 des 10 items
   * réels capturés). Sans ces listes, la cascade partirait dans le désordre.
   */
  orderedFigures: SpriteLayout[]
  orderedDropzones: SpriteLayout[]
}

/**
 * Seuls ces deux types de sprite sont instanciés par brain.js (656-664 et
 * 673-676) : tout autre type est silencieusement ignoré.
 */
const RENDERABLE_TYPES = new Set(["abstract", "numeric"])

/** Item -> scène, sur l'axe X. Le `/ 2` vient des formules de brain.js. */
function itemToStageX(x: number): number {
  return ((x / ITEM_WIDTH) * STAGE_WIDTH) / 2
}

/** Item -> scène, sur l'axe Y. Le signe inverse l'axe (Unity pointe vers le haut). */
function itemToStageY(y: number): number {
  return ((-y / ITEM_HEIGHT) * STAGE_HEIGHT) / 2
}

/**
 * Dispose une scène complète.
 *
 * Les figures et les dropzones sont rattachées à leur masque : leurs
 * coordonnées sont LOCALES, l'origine étant le coin haut-gauche de la boîte du
 * masque. C'est pour ça que brain.js n'y ajoute pas le recentrage `+ w/2`
 * qu'il applique aux tokens.
 */
export function layoutScene(data: RawSceneData): SceneLayout {
  const masks: MaskLayout[] = (data.masks ?? []).map((raw) => {
    // Taille de la boîte en px de scène
    const width = ((raw.width / ITEM_WIDTH) * STAGE_WIDTH) / 2
    const height = ((raw.height / ITEM_HEIGHT) * STAGE_HEIGHT) / 2

    // Le pivot de brain.js vaut la moitié de la taille : x/y désignent donc
    // bien le CENTRE de la boîte.
    const centerX = itemToStageX(raw.x) + STAGE_WIDTH / 2
    const centerY = itemToStageY(raw.y) + STAGE_HEIGHT / 2

    return {
      name: raw.name,
      type: raw.type,
      left: centerX - width / 2,
      top: centerY - height / 2,
      width,
      height,
      rotation: -raw.rotation * (Math.PI / 180),
      isNineSlice: NINE_SLICE_TYPES.has(raw.type),
      figures: [],
      dropzones: [],
    }
  })

  const byName = new Map(masks.map((m) => [m.name, m]))
  const orderedFigures: SpriteLayout[] = []
  const orderedDropzones: SpriteLayout[] = []

  /** Rattache un sprite enfant à son masque, en coordonnées locales. */
  const attach = (
    raw: RawSprite,
    index: number,
    kind: "figure" | "dropzone",
    target: (_mask: MaskLayout) => SpriteLayout[]
  ) => {
    const parent = raw.parent ? byName.get(raw.parent) : undefined
    if (!parent) return

    const sprite: SpriteLayout = {
      key: `${kind}:${raw.parent}:${raw.name}:${index}`,
      name: raw.name,
      type: (raw.type ?? "").toLowerCase(),
      x: itemToStageX(raw.x),
      y: itemToStageY(raw.y),
      size: SPRITE_SIZE,
      counterRotation: -parent.rotation,
    }
    target(parent).push(sprite)
    // Même objet dans les deux listes : le rendu passe par `masks[]`, les
    // animations par la liste à plat.
    ;(kind === "figure" ? orderedFigures : orderedDropzones).push(sprite)
  }

  ;(data.figures ?? []).forEach((raw, i) => {
    if (!RENDERABLE_TYPES.has((raw.type ?? "").toLowerCase())) return
    attach(raw, i, "figure", (m) => m.figures)
  })

  // Les dropzones n'ont pas de type : elles utilisent toujours la même image.
  ;(data.dropzones ?? []).forEach((raw, i) => {
    attach(raw, i, "dropzone", (m) => m.dropzones)
  })

  const tokens: SpriteLayout[] = (data.tokens ?? [])
    .filter((raw) => RENDERABLE_TYPES.has((raw.type ?? "").toLowerCase()))
    .map((raw) => ({
      key: `token:${raw.name}`,
      name: raw.name,
      type: (raw.type ?? "").toLowerCase(),
      x: itemToStageX(raw.x) + STAGE_WIDTH / 2,
      y: itemToStageY(raw.y) + STAGE_HEIGHT / 2,
      size: SPRITE_SIZE,
      counterRotation: 0,
    }))

  return { masks, tokens, orderedFigures, orderedDropzones }
}
